import React, { useState, useEffect } from 'react';
import { Loader2, Search, CheckCircle2, UserCheck } from 'lucide-react';

interface VerificationLoaderProps {
  isNewUser: boolean;
}

const VerificationLoader: React.FC<VerificationLoaderProps> = ({ isNewUser }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Steps for new user (with Perfios data fetch)
  const newUserSteps = [
    { icon: CheckCircle2, text: 'Verifying OTP', duration: 800 },
    { icon: Search, text: 'Fetching your information', duration: 1500 },
    { icon: UserCheck, text: 'Setting up your account', duration: 1000 },
  ];

  // Steps for existing user (just login)
  const existingUserSteps = [
    { icon: CheckCircle2, text: 'Verifying OTP', duration: 800 },
    { icon: UserCheck, text: 'Logging you in', duration: 1000 },
  ];

  const steps = isNewUser ? newUserSteps : existingUserSteps;

  useEffect(() => {
    if (currentStep < steps.length - 1) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, steps[currentStep].duration);

      return () => clearTimeout(timer);
    }
  }, [currentStep, steps]);

  const CurrentIcon = steps[currentStep].icon;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Icon Animation */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            {/* Outer rotating ring */}
            <div className="absolute inset-0 rounded-full border-4 border-intellend-green/30 animate-spin"
                 style={{ animationDuration: '3s' }} />
            
            {/* Inner pulsing circle */}
            <div className="relative bg-intellend-green/5 rounded-full p-6 animate-pulse">
              <CurrentIcon className="w-12 h-12 text-intellend-green" />
            </div>

            {/* Searching animation for "Fetching information" step */}
            {currentStep === 1 && isNewUser && (
              <div className="absolute -right-2 -top-2">
                <div className="bg-intellend-green rounded-full p-2 animate-bounce">
                  <Search className="w-4 h-4 text-white" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Current Step Text */}
        <div className="text-center space-y-3">
          <h3 className="text-xl font-semibold text-gray-900 animate-in fade-in slide-in-from-bottom-2 duration-500">
            {steps[currentStep].text}
          </h3>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-2 pt-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? 'w-8 bg-intellend-green'
                    : index < currentStep
                    ? 'w-2 bg-intellend-green/60'
                    : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Additional context text */}
          <p className="text-sm text-gray-500 pt-2">
            {isNewUser && currentStep === 1 
              ? 'Securely retrieving your details...'
              : 'Please wait a moment'}
          </p>
        </div>

        {/* Animated loading bar */}
        <div className="mt-6 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-intellend-green to-intellend-green/80 rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${((currentStep + 1) / steps.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default VerificationLoader;
